import { createContext, useContext, useEffect, type ReactNode } from "react";
import i18n, { loadLanguage, type AppLang } from "./index";

type LocaleContextValue = { locale: AppLang };

const LocaleContext = createContext<LocaleContextValue>({ locale: "sv" });

/**
 * Wrappa en route-komponent med detta för att tvinga ett specifikt språk.
 * Används av alla /en/* routes så att besökare ALWAYS får engelsk chrome
 * från första render — utan att vänta på localStorage-detektering.
 *
 * SSR-säker: själva renderingen påverkas inte här (i18next står kvar på
 * "sv" tills hydration); useEffect byter språk klient-side så HTML
 * matchar på första paint, sedan re-renderas trädet med engelska strings.
 */
export function LocaleProvider({
  locale,
  children,
}: {
  locale: AppLang;
  children: ReactNode;
}) {
  useEffect(() => {
    if (locale === "sv") return;
    void loadLanguage(locale).then(() => {
      if (i18n.resolvedLanguage !== locale) {
        void i18n.changeLanguage(locale);
      }
    });
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale }}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): AppLang {
  return useContext(LocaleContext).locale;
}
